import { areTheSameDates, formatDateDisplay, formatTime } from 'utils/datetime';

/**
 * @typedef {'lineIds' | 'fromCityIds' | 'toCityIds' | 'tripNumbers' | 'runNames' | 'departureRange'} FilterType
 */

/**
 * @typedef {object} CityFilterValue
 * @property {string} title
 * @property {string} [key]
 */

/**
 * @typedef {object} LineFilterValue
 * @property {string} code
 * @property {string} title
 * @property {string} [key]
 */

/**
 * @typedef {object} DepartureRangeFilterValue
 * @property {string} from ISO-8601.
 * @property {string} to ISO-8601.
 */

/**
 * @typedef {CityFilterValue | LineFilterValue | DepartureRangeFilterValue | string | number} FilterValue
 */

/**
 * @typedef {object} FilterEntry
 * @property {FilterValue} value
 * @property {FilterType} type
 */

export const DEADHEAD_DISABLED_FILTER_TYPES = new Set(['lineIds', 'tripNumbers', 'runNames']);

/**
 * Pipelines that turn a filter value into the visible chip label.
 * Keyed by `FilterType`.
 */
export const chipLabelPipelines = {
	/** @param {CityFilterValue} data */
	fromCityIds: (data) => data.title,
	/** @param {CityFilterValue} data */
	toCityIds: (data) => data.title,
	/** @param {LineFilterValue} data */
	lineIds: (data) => `${data.code} - ${data.title}`,
	/** @param {string | number} data */
	tripNumbers: (data) => String(data),
	/** @param {string} data */
	runNames: (data) => data,
	/** @param {DepartureRangeFilterValue} data */
	departureRange: (data) => {
		const { from, to } = data;
		if (areTheSameDates(from, to)) {
			return `${formatDateDisplay(from, { style: 'dotted' })} ${formatTime(from)}`;
		}
		return `${formatDateDisplay(from, { style: 'dotted' })} ${formatTime(from)} - ${formatDateDisplay(to, { style: 'dotted' })} ${formatTime(to)}`;
	},
};

/**
 * @typedef {object} FilterChipViewModel
 * @property {string} key Store map key.
 * @property {FilterType} type
 * @property {string} label Visible chip text.
 * @property {boolean} disabled True when the deadheads tab disables this type.
 */

/**
 * Build the view model for the chip list.
 *
 * @param {object} args
 * @param {Map<string, FilterEntry>} args.filtersMap
 * @param {boolean} args.isDeadheadsTabActive
 * @returns {FilterChipViewModel[]}
 */
export const buildFilterChipData = ({ filtersMap, isDeadheadsTabActive }) => {
	const disabledTypes = isDeadheadsTabActive ? DEADHEAD_DISABLED_FILTER_TYPES : new Set();

	return [...filtersMap.entries()].map(([key, entry]) => {
		const { value, type } = entry;
		return {
			key,
			type,
			label: chipLabelPipelines[type](value),
			disabled: disabledTypes.has(type),
		};
	});
};
